<template>
  <div class="page-container">
    <Loader v-if="isLoading" display="absolute"></Loader>
    <Alert
      v-if="errorMsgs.length"
      class="mt-20"
      type="danger"
      :messages="errorMsgs" />
  </div>

  <form ref="form" autocomplete="off" @submit.prevent="onSubmitForm">
    <section class="page-container">
      <div class="row s-flexdrive-booking">
        <div class="col-lg-8 b-flexdrive-booking">
          <div class="row">
            <!-- title -->
            <div class="col-lg-8 mb-30 b-flexdrive-booking__header">
              <span class="extrabold">{{ dictionary.personalHeader }}</span>
            </div>
            <div class="col-lg-4 mb-30 b-flexdrive-booking__required">
              <span class="small semibold">
                {{ dictionary.requiredFieldsText }}
              </span>
            </div>
          </div>

          <div class="row">
            <!-- title -->
            <div class="col-lg-6 mb-24 b-flexdrive-booking__title">
              <span>{{ dictionary.searchBarTitle }}</span>
            </div>
          </div>
          <div v-if="!departureIsCustom" class="row mb-15">
            <!-- search bar -->
            <div v-auto-animate class="col">
              <div
                class="form-pseudo-floating pb-0 b-booking-pick-location"
                :class="[
                  v$.locationDeparture.$error ? 'has-error' : '',
                  isFocusDeparture ? 'show' : '',
                ]"
                @click="onClickLocationDeparture">
                <label
                  for="searchLocationDeparture"
                  class="form-label"
                  @click.stop="onClickLocationDeparture">
                  <i class="icon-pin"></i>
                  <span>{{ dictionary.locationDepartureLabel }}</span>
                  <button
                    class="btn btn-info btn-icon-end btn-network"
                    type="button"
                    @click.stop="onClickNetworkDeparture">
                    <small>{{ dictionary.buttonNetworkText }}</small>
                    <i class="icon-pin-network"></i>
                  </button>
                </label>

                <MultiSelect
                  id="searchLocationDeparture"
                  ref="multiselectLocationDeparture"
                  v-model="locationDeparture"
                  autocomplete="off"
                  :searchable="true"
                  :clear-on-search="true"
                  :can-clear="true"
                  :options="locations"
                  :can-deselect="false"
                  :no-results-text="dictionary.noResultsText"
                  value-prop="Value"
                  label="Label"
                  :placeholder="dictionary.searchLocationPlaceholder"
                  :filter-results="false"
                  :no-options-text="dictionary.noOptionsText"
                  :clear-on-blur="false"
                  :class="{ 'has-error': v$.locationDeparture.$error }"
                  @search-change="onSearchChange"
                  @click.stop
                  @open="isFocusDeparture = true"
                  @close="isFocusDeparture = false" />
              </div>

              <BookingBarNetwork
                v-if="showNetworkDeparture"
                type="departure"
                @change-location="onChangeLocation" />
            </div>
            <div v-if="v$.locationDeparture.$error">
              <div v-if="v$.locationDeparture.required.$invalid" class="error">
                {{ dictionary.locationDepartureRequired }}
              </div>
            </div>
          </div>
          <div v-if="!departureIsCustom">
            <div v-if="showDatePicker || showRentalDuration" class="row">
              <div v-if="showDatePicker" class="col-7 col-sm-6 mb-15">
                <BookingBarDatePicker
                  :key="'departure' + key"
                  type="departure"
                  :label="dictionary.dateDepartureLabel"
                  :hour-label="dictionary.hourDepartureLabel"
                  :selected-date="dateDepartureFormated"
                  :selected-hour="hourDeparture"
                  :selected-minutes="minutesDeparture"
                  :hour-data-source="hourDataSource"
                  :minutes-data-source="minutesDataSource"
                  :has-error="v$.dateDeparture.$error"
                  @change-clock="onChangeClock" />
                <div v-if="v$.dateDeparture.$error">
                  <div v-if="v$.dateDeparture.required.$invalid" class="error">
                    {{ dictionary.dateDepartureRequired }}
                  </div>
                </div>
              </div>
              <div v-if="showRentalDuration" class="col-5 col-sm-6 mb-15">
                <select
                  id="duration"
                  v-model="rentalDuration"
                  class="form-select"
                  :class="{ 'has-error': v$.rentalDuration.$error }">
                  <option value="" selected disabled>
                    {{ dictionary.rentalDurationPlaceholder }}
                  </option>
                  <option
                    v-for="(item, index) in rentalDurationOptions"
                    :key="index"
                    :value="item.value">
                    {{ item.text }}
                  </option>
                </select>
                <div v-if="v$.rentalDuration.$error">
                  <div v-if="v$.rentalDuration.required.$invalid" class="error">
                    {{ dictionary.rentalDurationRequired }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="!departureIsCustom && showCustomPickupLocation"
            class="separator mb-30">
            <div class="line"></div>
            <div class="title">{{ dictionary.orLabel }}</div>
            <div class="line"></div>
          </div>
          <div v-if="showCustomPickupLocation" class="b-delivery mb-40">
            <div class="b-delivery__title">{{ dictionary.deliveryLabel }}</div>
            <div class="b-delivery__conts">
              <div class="b-delivery__image">
                <img
                  src="/imgs/misc/placeholder-image.png"
                  class="lazy"
                  :data-src="
                    inputPickupLocationLogo ? inputPickupLocationLogo.url : ''
                  "
                  :alt="
                    inputPickupLocationLogo ? inputPickupLocationLogo.alt : ''
                  " />
              </div>
              <div class="b-delivery__text">
                <div class="b-delivery__text__title">
                  {{ dictionary.deliveryTitle }}
                </div>
                <div
                  class="b-delivery__text__subtitle"
                  v-html="dictionary.deliverySubtitle"></div>
                <div class="b-delivery__toggle">
                  <input
                    id="departureIsCustom"
                    v-model="departureIsCustom"
                    class="form-check-input"
                    type="checkbox" />
                  <label for="departureIsCustom" class="form-check-label">
                    {{ dictionary.deliveryToggleLabel }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div v-show="departureIsCustom" class="mb-15">
            <div class="col-lg-12">
              <label for="autocomplete" class="form-label">
                {{ dictionary.inputLocationLabel }}
              </label>
              <input
                id="autocomplete"
                ref="autocompleteInput"
                class="form-control"
                :class="{ 'has-error': v$.customAddress.$error }"
                name="autocomplete"
                type="text"
                @keypress.enter.prevent
                @input="onInputAddress"
                @blur="v$.customAddress.$touch" />

              <div v-if="v$.customAddress.$error">
                <div v-if="v$.customAddress.required.$invalid" class="error">
                  {{ dictionary.inputLocationRequired }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="departureIsCustom">
            <div v-if="showDatePicker || showRentalDuration" class="row">
              <div v-if="showDatePicker" class="col-7 col-sm-6 mb-15">
                <BookingBarDatePicker
                  :key="'departure' + key"
                  type="departure"
                  :label="dictionary.dateDepartureLabel"
                  :hour-label="dictionary.hourDepartureLabel"
                  :selected-date="dateDepartureFormated"
                  :selected-hour="hourDeparture"
                  :selected-minutes="minutesDeparture"
                  :hour-data-source="hourDataSource"
                  :minutes-data-source="minutesDataSource"
                  :has-error="v$.dateDeparture.$error"
                  @change-clock="onChangeClock" />
                <div v-if="v$.dateDeparture.$error">
                  <div v-if="v$.dateDeparture.required.$invalid" class="error">
                    {{ dictionary.dateDepartureRequired }}
                  </div>
                </div>
              </div>
              <div v-if="showRentalDuration" class="col-5 col-sm-6 mb-15">
                <select
                  id="duration"
                  v-model="rentalDuration2"
                  class="form-select"
                  :class="{ 'has-error': v$.rentalDuration2.$error }">
                  <option value="" selected disabled>
                    {{ dictionary.rentalDurationPlaceholder }}
                  </option>
                  <option
                    v-for="(item, index) in rentalDurationOptions"
                    :key="index"
                    :value="item.value">
                    {{ item.text }}
                  </option>
                </select>
                <div v-if="v$.rentalDuration2.$error">
                  <div
                    v-if="v$.rentalDuration2.required.$invalid"
                    class="error">
                    {{ dictionary.rentalDurationRequired }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr class="mb-40" />

          <div class="row">
            <!-- title -->
            <div class="col-lg-6 mb-24 b-flexdrive-booking__title">
              <span class="extrabold">{{ dictionary.personalInfoTitle }}</span>
            </div>
          </div>

          <div class="row gx-5">
            <!-- name -->
            <div class="col-lg-6 mb-30">
              <label for="name" class="form-label">
                {{ dictionary.nameLabel }}
              </label>
              <input
                id="name"
                v-model.trim="name"
                class="form-control"
                :class="{ 'has-error': v$.name.$error }"
                name="name"
                type="text"
                @blur="v$.name.$touch" />

              <div v-if="v$.name.$error">
                <div v-if="v$.name.required.$invalid" class="error">
                  {{ dictionary.nameRequired }}
                </div>
                <!-- prettier-ignore -->
                <div
                  v-if="v$.name.minLength.$invalid || v$.name.maxLength.$invalid"
                  class="error">
                  {{ dictionary.nameLength }}
                </div>
              </div>
            </div>
            <!-- surname -->
            <div class="col-lg-6 mb-30">
              <label for="surname" class="form-label">
                {{ dictionary.surnameLabel }}
              </label>
              <input
                id="surname"
                v-model.trim="surname"
                class="form-control"
                :class="{ 'has-error': v$.surname.$error }"
                name="surname"
                type="text"
                @blur="v$.surname.$touch" />

              <div v-if="v$.surname.$error">
                <div v-if="v$.surname.required.$invalid" class="error">
                  {{ dictionary.surnameRequired }}
                </div>
                <!-- prettier-ignore -->
                <div
                  v-if="v$.surname.minLength.$invalid || v$.surname.maxLength.$invalid"
                  class="error">
                  {{ dictionary.surnameLength }}
                </div>
              </div>
            </div>
          </div>
          <div class="row gx-5">
            <!-- email -->
            <div class="col-lg-6 mb-30">
              <label for="email" class="form-label">
                {{ dictionary.emailLabel }}
              </label>
              <input
                id="email"
                v-model.trim="email"
                class="form-control"
                :class="{ 'has-error': v$.email.$error }"
                name="email"
                type="email"
                @blur="v$.email.$touch" />

              <div v-if="v$.email.$error">
                <div v-if="v$.email.required.$invalid" class="error">
                  {{ dictionary.emailRequired }}
                </div>
                <div v-if="v$.email.email.$invalid" class="error">
                  {{ dictionary.emailRegex }}
                </div>
                <!-- prettier-ignore -->
                <div
                  v-if="v$.email.minLength.$invalid || v$.email.maxLength.$invalid"
                  class="error">
                  {{ dictionary.emailLength }}
                </div>
              </div>
            </div>
            <!-- confirmEmail -->
            <div class="col-lg-6 mb-30">
              <label for="confirmEmail" class="form-label">
                {{ dictionary.confirmEmailLabel }}
              </label>
              <input
                id="confirmEmail"
                v-model.trim="confirmEmail"
                class="form-control"
                :class="{ 'has-error': v$.confirmEmail.$error }"
                name="confirmEmail"
                type="text"
                @blur="v$.confirmEmail.$touch"
                @paste.prevent
                @drop.prevent />

              <div v-if="v$.confirmEmail.$error">
                <div v-if="v$.confirmEmail.required.$invalid" class="error">
                  {{ dictionary.emailRequired }}
                </div>
                <div v-if="v$.confirmEmail.email.$invalid" class="error">
                  {{ dictionary.emailRegex }}
                </div>
                <!-- prettier-ignore -->
                <div
                  v-if="v$.confirmEmail.minLength.$invalid || v$.confirmEmail.maxLength.$invalid"
                  class="error">
                  {{ dictionary.emailLength }}
                </div>
                <div
                  v-if="v$.confirmEmail.sameAsPassword.$invalid"
                  class="error">
                  {{ dictionary.confirmEmailSameAs }}
                </div>
              </div>
            </div>
          </div>
          <div class="row gx-5">
            <!-- mobilePhone -->
            <div class="col-lg-6 mb-30">
              <label for="mobilePhone" class="form-label">
                {{ dictionary.mobilePhoneLabel }}
              </label>

              <div class="phone-group">
                <MultiSelect
                  id="mobileDialingCode"
                  v-model="mobileDialingCode"
                  autocomplete="nope"
                  :searchable="true"
                  :clear-on-search="true"
                  :options="dialingCodes"
                  :can-deselect="false"
                  :can-clear="false"
                  :no-results-text="dictionary.noResultsText"
                  label="text"
                  :class="{
                    'has-error': v$.mobileDialingCode.$error,
                  }">
                  <template #singlelabel="{ value }">
                    <div class="multiselect-single-label">
                      <img
                        class="multiselect-icon-currency"
                        :src="value.icon"
                        :alt="value.search" />
                      {{ value.text }}
                    </div>
                  </template>

                  <template #option="{ option }">
                    <img class="multiselect-icon-currency" :src="option.icon" />
                    {{ option.text }}
                  </template>
                </MultiSelect>

                <input
                  id="mobilePhone"
                  v-model.trim="mobilePhone"
                  class="form-control"
                  :class="{ 'has-error': v$.mobilePhone.$error }"
                  name="mobilePhone"
                  type="tel"
                  @blur="v$.mobilePhone.$touch" />
              </div>

              <div v-if="v$.mobilePhone.$error">
                <div v-if="v$.mobilePhone.required.$invalid" class="error">
                  {{ dictionary.mobilePhoneRequired }}
                </div>
                <!-- prettier-ignore -->
                <div
                  v-if="v$.mobilePhone.minLength.$invalid || v$.mobilePhone.maxLength.$invalid"
                  class="error">
                  {{ dictionary.mobilePhoneLength }}
                </div>
              </div>
            </div>
            <!-- landPhone -->
            <div class="col-lg-6 mb-30">
              <label for="landPhone" class="form-label">
                {{ dictionary.landPhoneLabel }}
              </label>

              <div class="phone-group">
                <MultiSelect
                  id="landDialingCode"
                  v-model="landDialingCode"
                  autocomplete="nope"
                  :searchable="true"
                  :clear-on-search="true"
                  :options="dialingCodes"
                  :can-deselect="false"
                  :can-clear="false"
                  :no-results-text="dictionary.noResultsText"
                  label="text"
                  :class="{
                    'has-error': v$.landDialingCode.$error,
                  }">
                  <template #singlelabel="{ value }">
                    <div class="multiselect-single-label">
                      <img
                        class="multiselect-icon-currency"
                        :src="value.icon"
                        :alt="value.search" />
                      {{ value.text }}
                    </div>
                  </template>

                  <template #option="{ option }">
                    <img class="multiselect-icon-currency" :src="option.icon" />
                    {{ option.text }}
                  </template>
                </MultiSelect>

                <input
                  id="landPhone"
                  v-model.trim="landPhone"
                  class="form-control"
                  :class="{ 'has-error': v$.landPhone.$error }"
                  name="landPhone"
                  type="tel"
                  @blur="v$.landPhone.$touch" />
              </div>

              <div v-if="v$.landPhone.$error">
                <div v-if="v$.landPhone.required.$invalid" class="error">
                  {{ dictionary.landPhoneRequired }}
                </div>
                <!-- prettier-ignore -->
                <div
                  v-if="v$.landPhone.minLength.$invalid || v$.landPhone.maxLength.$invalid"
                  class="error">
                  {{ dictionary.landPhoneLength }}
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-40">
            <div class="col form-check">
              <input
                id="usePersonalData"
                v-model="usePersonalData"
                name="usePersonalData"
                type="checkbox"
                class="form-check-input"
                autocomplete="off" />
              <label class="form-check-label" for="usePersonalData">
                <div v-html="dictionary.usePersonalDataLabel"></div>
              </label>
            </div>
          </div>

          <div v-if="showMilesAndBonus" class="row b-flexdrive-booking__bonus">
            <div class="col">
              <div>
                <img
                  src="/imgs/misc/placeholder-image.png"
                  class="lazy object-fit-cover"
                  :data-src="milesAndBonusLogo ? milesAndBonusLogo.url : ''"
                  :alt="milesAndBonusLogo ? milesAndBonusLogo.alt : ''" />
              </div>
              <div>
                <p class="bold mt-20 mb-12 title">
                  {{ dictionary.bonusCodeTitle }}
                </p>
                <p class="subtitle">
                  {{ dictionary.bonusCodeSubTitle }}
                </p>
              </div>

              <div>
                <label for="bonusCode" class="form-label">
                  {{ dictionary.bonusCodeSubLabel }}
                </label>
                <input
                  id="bonusCode"
                  v-model.trim="bonusCode"
                  class="form-control"
                  :class="{ 'has-error': v$.bonusCode.$error }"
                  name="name"
                  type="text"
                  @blur="v$.bonusCode.$touch" />
              </div>
              <div v-if="v$.bonusCode.$error">
                <div
                  v-if="v$.bonusCode.milesAndBonusRegex.$invalid"
                  class="error">
                  {{ dictionary.bonusCodeInvalid }}
                </div>
              </div>
            </div>
          </div>

          <!-- comments -->
          <div class="mt-40 b-flexdrive-booking__comments">
            <div class="row">
              <label for="comments" class="form-label mb-24">
                {{ dictionary.commentsLabel }}
              </label>
            </div>

            <div class="row">
              <div class="col mb-15">
                <!-- comments text area -->
                <textarea
                  id="comments"
                  v-model="comments"
                  class="form-control"
                  :class="{ 'has-error': v$.comments.$error }"
                  :placeholder="dictionary.commentsPlaceholder"
                  :maxlength="commentsMaxLength"
                  @blur="v$.comments.$touch"></textarea>

                <div v-if="v$.comments.$error">
                  <div v-if="v$.comments.maxLength.$invalid" class="error">
                    {{ dictionary.commentsLength }}
                  </div>
                </div>
              </div>
            </div>

            <!-- comments characters -->
            <div class="row">
              <div class="col d-flex justify-content-end mb-40">
                <small class="bold">
                  <span v-if="commentsLength">{{ commentsLength }} /</span>
                  {{ commentsMaxLength }}
                  {{ dictionary.commentsCharacters }}
                  {{ dictionary.charactersLabel }}
                </small>
              </div>
            </div>
          </div>

          <div class="row mb-60">
            <!-- accept terms -->
            <div class="col">
              <input
                id="accept-terms"
                v-model="terms"
                class="form-check-input"
                :class="{ 'has-error': v$.terms.$error }"
                name="AcceptTerms"
                type="checkbox"
                autocomplete="off" />
              <label for="accept-terms" class="form-check-label semibold">
                <div v-html="dictionary.termsLabel"></div>
              </label>
              <div v-if="v$.terms.$error">
                <div v-if="v$.terms.checked.$invalid" class="error">
                  {{ dictionary.termsRequired }}
                </div>
              </div>
            </div>
          </div>
          <!-- buttons -->
          <div class="row b-flexdrive-booking__buttons">
            <div class="col-lg-4 mb-60 recaptcha">
              <div id="fd-recaptcha" class="mb-20"></div>

              <div v-if="v$.reCaptchaResponse.$error">
                <div
                  v-if="v$.reCaptchaResponse.required.$invalid"
                  class="error">
                  {{ dictionary.reCaptchaRequired }}
                </div>
              </div>
            </div>
            <div class="col-lg-5 offset-lg-3 col-xl-6 offset-xl-2 mb-20">
              <button
                type="submit"
                class="btn btn-primary btn-full-width"
                :disabled="isLoading">
                {{ dictionary.buttonSubmitText }}
              </button>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <FlexVehicleResume
            v-if="carObj"
            :item="carObj"
            @on-toggle-vat="onToggleVat" />
        </div>
      </div>
      <Anchor
        v-if="clickToCall"
        :item="clickToCall"
        :hide-text="true"
        aria-label="Click to call"
        class="e-call-us body-fixed">
        <i class="icon-phone-call"></i>
      </Anchor>
    </section>
  </form>
</template>
<script>
import constants from './../../lib/constants'
import * as lib from './../../lib/lib'
import axios from 'axios'
import Alert from '../components/common/Alert.vue'
import Loader from '../components/common/Loader.vue'
import MultiSelect from '@vueform/multiselect'
import useVuelidate from '@vuelidate/core'
import {
  email,
  helpers,
  maxLength,
  minLength,
  required,
  requiredIf,
  sameAs,
} from '@vuelidate/validators'
import FlexVehicleResume from '../components/flexdrive/FlexVehicleResume.vue'
import BookingBarNetwork from './../components/BookingBarNetwork.vue'
import Anchor from './../components/common/Anchor.vue'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import BookingBarDatePicker from './../components/BookingBarDatePicker.vue'

const milesAndBonusRegex = helpers.regex(/^1\d{8}$/)

export default {
  components: {
    Anchor,
    Loader,
    Alert,
    MultiSelect,
    FlexVehicleResume,
    BookingBarNetwork,
    BookingBarDatePicker,
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      key: uuidv4(),
      scrollOffsetY: 125,
      isLoading: false,
      errorMsgs: [],

      // data
      locationDeparture: '',
      customAddress: '',
      dateDeparture: null,
      hourDataSource: [],
      minutesDataSource: [],
      hourDeparture: '',
      minutesDeparture: '',
      rentalDuration: '',
      rentalDuration2: '',
      rentalDurationOptions: [],

      // form
      title: '',
      step: 2,
      steps: [],

      name: '',
      surname: '',
      email: '',
      confirmEmail: '',

      mobileDialingCode: '',
      mobilePhone: '',
      landDialingCode: '',
      landPhone: '',
      usePersonalData: false,

      bonusCode: '',

      comments: '',
      commentsMaxLength: 200,

      vatIncluded: false,
      carObj: null,
      terms: false,
      departureIsCustom: false,

      // umbraco extra options
      showMilesAndBonus: false,
      showCustomPickupLocation: false,
      showDatePicker: false,
      showRentalDuration: false,

      reCaptchaResponse: '',
      reCaptchaWidget: null,

      response: { title: '', text: '' },

      autocomplete: null,

      // data sources
      dialingCodes: [],
      legalTerms: [],
      vehicleBenefits: [],

      countries: [],
      cities: [],
      locations: [],
      // helpers
      showNetworkDeparture: false,
      searchMinLength: 3,

      isFocusDeparture: false,
      clickToCall: null,

      // search timeout
      timer: undefined,
      delay: 500,
    }
  },
  validations() {
    return {
      bonusCode: {
        milesAndBonusRegex,
      },
      locationDeparture: { required: requiredIf(!this.departureIsCustom) },
      customAddress: {
        required: requiredIf(this.departureIsCustom),
      },
      dateDeparture: { required: requiredIf(this.showDatePicker) },
      hourDeparture: { required: requiredIf(this.showDatePicker) },
      minutesDeparture: { required: requiredIf(this.showDatePicker) },
      rentalDuration: {
        required: requiredIf(
          !this.departureIsCustom && this.showRentalDuration
        ),
      },
      rentalDuration2: {
        required: requiredIf(this.departureIsCustom && this.showRentalDuration),
      },
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
      },
      surname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
      },
      email: {
        required,
        email,
        minLength: minLength(6),
        maxLength: maxLength(50),
      },
      confirmEmail: {
        required,
        email,
        minLength: minLength(6),
        maxLength: maxLength(50),
        sameAsPassword: sameAs(this.email),
      },
      mobileDialingCode: {
        required,
      },
      mobilePhone: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(40),
      },
      landDialingCode: {
        required,
      },
      landPhone: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(40),
      },
      comments: {
        maxLength: maxLength(this.commentsMaxLength),
      },
      terms: {
        checked: (value) => value === true,
      },
      reCaptchaResponse: {
        required,
      },
    }
  },
  computed: {
    inputPickupLocationLogo() {
      const { inputPickupLocationLogo } = lib.getData('flexDriveBookingData')
      if (inputPickupLocationLogo) {
        return {
          url: inputPickupLocationLogo.url,
          alt: inputPickupLocationLogo.alt,
        }
      }
      return null
    },
    milesAndBonusLogo() {
      const { milesAndBonusLogo } = lib.getData('flexDriveBookingData')
      if (milesAndBonusLogo) {
        return { url: milesAndBonusLogo.url, alt: milesAndBonusLogo.alt }
      }
      return null
    },
    commentsLength() {
      return this.comments.length
    },
    dateDepartureFormated() {
      return this.dateDeparture
        ? moment(this.dateDeparture, this.configuration.dateFormat).toDate()
        : null
    },
  },
  watch: {
    locationDeparture(newValue) {
      const country = this.getLocationCountry(newValue)
      this.countryDeparture = country
    },
  },
  mounted() {
    const {
      title,
      steps,
      vehicle,
      vehicleBenefits,
      vatIncluded,
      dialingCodes,
      response,
      clickToCall,
      showMilesAndBonus,
      showCustomPickupLocation,
      showDatePicker,
      showRentalDuration,
      rentalDurationOptions,
      hours,
      minutes,
    } = lib.getData('flexDriveBookingData')

    this.hourDataSource = hours
    this.minutesDataSource = minutes

    this.title = title
    this.steps = steps
    this.dialingCodes = dialingCodes
    this.carObj = vehicle
    this.vehicleBenefits = vehicleBenefits
    this.response = response
    this.vatIncluded = vatIncluded
    this.clickToCall = clickToCall
    this.showMilesAndBonus = showMilesAndBonus
    this.showCustomPickupLocation = showCustomPickupLocation
    this.showDatePicker = showDatePicker
    this.showRentalDuration = showRentalDuration
    this.rentalDurationOptions = rentalDurationOptions

    this.setPreselectedOptions()

    document.addEventListener('recaptcha-ready', () => {
      this.iniReCaptcha()
    })

    document.addEventListener('google-map-ready', () => {
      this.initAutocomplete()
    })

    this.$nextTick(() => {
      lib.scrollToTop()
    })
  },
  methods: {
    onInputAddress() {
      this.customAddress = ''
    },

    onClickLocationDeparture() {
      this.$refs.multiselectLocationDeparture.open()
      document.getElementById('searchLocationDeparture').focus()
    },

    onSearchChange(query) {
      if (query.length < this.searchMinLength) {
        return false
      }

      clearTimeout(this.timer)

      this.timer = setTimeout(() => {
        const postData = { term: query }
        axios
          .post(this.configuration.endpointSearchLocations, postData)
          .then((response) => {
            const { Locations } = response.data
            this.locations = Locations
            this.handleSuccess()
          })
          .catch((errors) => {
            this.handleError([this.dictionary.defaultErrorMessage])
            console.error(errors)
          })
      }, this.delay)
    },

    onClickNetworkDeparture() {
      this.showNetworkDeparture = !this.showNetworkDeparture
    },

    onChangeLocation(value, options, type) {
      this.locations = options

      if (type === constants.location.departure) {
        this.locationDeparture = value
        this.showNetworkDeparture = false
      }
    },

    getLocationCountry(value) {
      const entry = this.locations.find((i) => i.Value === value)
      return entry ? entry.Country : ''
    },

    setPreselectedOptions() {
      const entry = this.dialingCodes.find((i) => i.selected)
      this.dialingCode =
        this.mobileDialingCode =
        this.landDialingCode =
          entry ? entry.value : ''

      const { hours, minutes } = lib.getData('flexDriveBookingData')

      // hours
      const entry2 = hours.find((i) => i.selected)
      if (entry2) {
        this.hourDeparture = entry2.value
      }
      // minutes
      const entry3 = minutes.find((i) => i.selected)
      if (entry3) {
        this.minutesDeparture = entry3.value
      }
    },

    onChangeClock(date, hour, minutes, type) {
      if (type === constants.location.departure) {
        this.dateDeparture = date
        this.hourDeparture = hour
        this.minutesDeparture = minutes
        // do not remove line below
        this.key = uuidv4()
      }
    },

    onToggleVat() {
      this.vatIncluded = !this.vatIncluded
    },

    callbackReCaptcha(response) {
      this.reCaptchaResponse = response
    },

    expiredCallbackReCaptha() {
      this.reCaptchaResponse = ''
    },

    iniReCaptcha() {
      document.getElementById('fd-recaptcha').innerHTML = ''

      const grecaptcha = window.grecaptcha

      this.reCaptchaWidget = grecaptcha.render('fd-recaptcha', {
        sitekey: this.configuration.reCaptchaPublicKey,
        callback: this.callbackReCaptcha,
        'expired-callback': this.expiredCallbackReCaptha,
      })
    },

    resetReCaptcha() {
      this.reCaptchaResponse = ''
      window.grecaptcha.reset(this.reCaptchaWidget)
    },

    onSubmitForm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      if (this.isLoading) {
        return false
      }

      this.isLoading = true

      let inputLocationLatLng = ''

      if (this.autocomplete && this.autocomplete.getPlace()) {
        const location = this.autocomplete.getPlace().geometry.location
        inputLocationLatLng = `${location.lat()},${location.lng()}`
      }

      const pickupDate = this.dateDeparture
        ? moment(this.dateDeparture).format(this.configuration.dateFormat)
        : null

      const formData = new FormData()

      formData.append('UseVehiclePriceVat', this.vatIncluded ? '1' : '0')
      formData.append('PickupLocationCode', this.locationDeparture)
      formData.append('PickupLocationCountryCode', this.countryDeparture)
      formData.append('DisplayDate', this.showDatePicker)
      formData.append(
        'PickupDate',
        this.showDatePicker ? pickupDate : this.getCurrentDate()
      )
      formData.append(
        'PickupHour',
        this.showDatePicker ? this.hourDeparture : this.getCurrentHours()
      )
      formData.append(
        'PickupMinutes',
        this.showDatePicker ? this.minutesDeparture : this.getCurrentMinutes()
      )
      formData.append(
        'Duration',
        this.rentalDuration ? this.rentalDuration : '1'
      )
      formData.append('DisplayRentalDuration', this.showRentalDuration)
      formData.append('PlacesInput', this.departureIsCustom)
      formData.append('PickupLocationAddress', this.customAddress)
      formData.append('InputLocationAddress', this.customAddress)
      formData.append('InputLocation', inputLocationLatLng)
      formData.append('DisplayDate2', this.showDatePicker)
      formData.append(
        'PickupDate2',
        this.showDatePicker ? pickupDate : this.getCurrentDate()
      )
      formData.append(
        'PickupHour2',
        this.showDatePicker ? this.hourDeparture : this.getCurrentHours()
      )
      formData.append(
        'PickupMinutes2',
        this.showDatePicker ? this.minutesDeparture : this.getCurrentMinutes()
      )
      formData.append(
        'Duration2',
        this.rentalDuration2 ? this.rentalDuration2 : '1'
      )
      formData.append('DisplayRentalDuration2', this.showRentalDuration)
      formData.append('Name', this.name)
      formData.append('Surname', this.surname)
      formData.append('Email', this.email)
      formData.append('ConfirmEmail', this.confirmEmail)
      formData.append('MobileDialingCode', this.mobileDialingCode)
      formData.append('MobilePhone', this.mobilePhone)
      formData.append('LandDialingCode', this.landDialingCode)
      formData.append('LandPhone', this.landPhone)
      formData.append('UsePersonalData', this.usePersonalData)
      formData.append('MilesAndBonusCardNumber', this.bonusCode)
      formData.append('Comments', this.comments)
      formData.append('SendSmsMessages', 'False')
      formData.append('AcceptTerms', this.terms)
      formData.append('g-recaptcha-response', this.reCaptchaResponse)
      formData.append('VehicleId', this.carObj.id)

      axios
        .post(this.configuration.endpointSubmitData, formData)
        .then((response) => {
          const { hasError, errorMessages, data } = response.data

          // response has error
          if (hasError) {
            this.handleError(errorMessages)
            return false
          }

          this.handleSuccess()
          window.location.assign(data.redirectUrl)
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    getCurrentDate() {
      const date = new Date()

      const currentDay = String(date.getDate()).padStart(2, '0')

      const currentMonth = String(date.getMonth() + 1).padStart(2, '0')

      const currentYear = date.getFullYear()

      return `${currentDay}/${currentMonth}/${currentYear}`
    },

    getCurrentHours() {
      const date = new Date()

      const currentHours = String(date.getHours())
        .padStart(2, '0')
        .padEnd(4, '0')

      return currentHours
    },

    getCurrentMinutes() {
      const date = new Date()

      const currentMinutes = String(date.getMinutes()).padStart(2, '0')

      return currentMinutes
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false
      this.$nextTick(() => {
        lib.scrollToError(100)
      })
    },

    initAutocomplete() {
      const autocompleteId = document.getElementById('autocomplete')

      const options = {
        types: ['address'],
        componentRestrictions: { country: this.configuration.country },
      }
      // eslint-disable-next-line no-undef
      this.autocomplete = new google.maps.places.Autocomplete(
        autocompleteId,
        options
      )

      this.autocomplete.addListener('place_changed', this.onPlaceChanged)
    },

    onPlaceChanged() {
      const place = this.autocomplete.getPlace()
      if (place) {
        this.customAddress = place.formatted_address
      }
    },

    onClick(item, index) {
      if (this.hasLink(item, index)) {
        window.location.assign(item.link.url)
      }
    },

    hasLink(item, index) {
      return (
        lib.hasValue(item.link?.url) &&
        this.isPrevious(index) &&
        this.step + 1 !== this.steps.length
      )
    },

    getClassName(item, index) {
      const classNames = []

      if (this.isActive(index)) {
        classNames.push('is-active')
      }

      if (this.isPrevious(index)) {
        classNames.push('is-previous')
      }

      if (this.hasLink(item, index)) {
        classNames.push('has-link')
      }

      return classNames.join(' ')
    },

    isActiveBar(index) {
      return this.step >= index + 1
    },

    isActive(index) {
      return index === Number(this.step)
    },

    isPrevious(index) {
      return index < Number(this.step)
    },
  },
}
</script>
<style lang="scss" scoped>
.separator {
  display: flex;
  flex-wrap: nowrap;
  justify-items: center;
  align-items: center;
  width: 100%;
  gap: 10px;

  .title {
    font-weight: 700;
  }
  .line {
    flex: 1;
    height: 1px;
    background-color: #d8d8d8;
  }
}
</style>
